import { usePlayerActions } from "@utils/player/core/usePlayer";
import type { Search } from "@libs/wire";
import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import OptionsButton from "@components/SongOptions/OptionsButton";
import { useTheme } from "@theme";
import UserAvatar from "@components/UserAvatar";
import { useSearchMutations } from "@hooks/trpc/useSearch";
import { useAuth } from "@utils/firebaseAuth/auth";

import * as rudderstack from "@utils/rudderstack";
import { router } from "expo-router";
import * as React from "react";
import {
  GestureResponderEvent,
  Keyboard,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { ExclusiveRibbon } from "@libs/shared-ui/native";
import { useListUnlockedPrivateSongs } from "@hooks/trpc/useListUnlockedPrivateSongs";
import { prefetchAccessDetailHeroImage } from "@utils/accesses/access";
interface SearchResultProps {
  item: Search;
  onNavigateAway?: () => void;
  /** The result was picked from a list of search results. */
  onSelect?: () => void;
}

const TransitionTrigger = ({
  children,
  item,
  onPress,
  onSelect,
}: {
  children: React.ReactNode;
  item: Search;
  onPress: () => void;
  onSelect?: () => void;
}) => {
  const unlockedPrivateSongIds = useListUnlockedPrivateSongs();
  if (
    item.type === "song" &&
    item.private &&
    item.accessId &&
    !unlockedPrivateSongIds?.includes(item.id)
  ) {
    return (
      <LPressable
        onPress={() => {
          onSelect?.();
          prefetchAccessDetailHeroImage(item.albums.at(0)?.image);
          router.push(`/access/${item.accessId}`);
        }}
        sentryEventName="SearchResult"
      >
        {children}
      </LPressable>
    );
  }
  return (
    <LPressable sentryEventName="search_result_pressed" onPress={onPress}>
      {children}
    </LPressable>
  );
};

const SearchResult = ({
  item,
  onNavigateAway,
  onSelect,
}: SearchResultProps) => {
  return (
    <StandardSearchResult
      item={item}
      onNavigateAway={onNavigateAway}
      onSelect={onSelect}
    />
  );
};

const StandardSearchResult = ({
  item,
  onNavigateAway,
  onSelect,
}: SearchResultProps) => {
  const playerActions = usePlayerActions();

  const { firebaseUser: user } = useAuth();
  const { addSearch } = useSearchMutations();
  const unlockedPrivateSongIds = useListUnlockedPrivateSongs();
  const playFromCollection = playerActions.playFromCollection;

  const onPress = async () => {
    onNavigateAway?.();
    onSelect?.();

    addSearch.mutate({
      type: item.type,
      id: item.id,
    });

    if (item.type === "song") {
      playFromCollection({
        songId: item.id,
        collectionType: "album",
        collectionId: item.albums.at(0)?.id ?? "",
        collectionName: item.albums.at(0)?.title,
      });
    } else {
      router.navigate({
        pathname: "/(app)/(tabs)/explore/[collectionType]/[collectionId]",
        params: {
          collectionType: item.type,
          collectionId: item.id,
        },
      });
    }
    Keyboard.dismiss();
  };

  const { type } = item;

  let title = "";
  let subtitle = "";
  let images: (string | undefined | null)[] = [""];
  let blurhash: string | undefined | null;

  switch (type) {
    case "song": {
      images = [item.albums.at(0)?.image];
      title = item.title;
      subtitle = `Song • ${item.artists.map((artist) => artist.name)?.join(", ") ?? ""}`;
      break;
    }
    case "album": {
      images = [item.image];
      title = item.title;
      subtitle = `Album • ${item.artistDisplayText}`;
      break;
    }
    case "artist": {
      images = [item.image];
      title = item.name;
      subtitle = "Artist";
      break;
    }
    case "playlist": {
      blurhash = item.blurhash;
      images = item.images || [];
      title = item.title;
      const isOwner = item.user !== null && item.user.id === user?.uid;
      if (isOwner) {
        subtitle = "Playlist • You";
      } else {
        subtitle = `Playlist • ${item.user?.name ?? "Unknown"}`;
      }
      break;
    }
  }

  const hasSubtitle = subtitle.length > 0;

  const isLocked =
    item.type === "song" &&
    item.private &&
    !unlockedPrivateSongIds?.includes(item.id);

  return (
    <TransitionTrigger item={item} onPress={onPress} onSelect={onSelect}>
      <LBox flex={1}>
        <LBox
          mb="s1"
          py="s1"
          pl="s2"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          borderRadius="s"
        >
          <LBox flexDirection="row" alignItems="center" flex={1}>
            <LBox mr="s3">
              {type === "artist" ? (
                <UserAvatar
                  image={images.at(0)}
                  size={43}
                  borderColor="buttonTextPrimary"
                  imageSize="small"
                  blurhash={blurhash}
                />
              ) : (
                <LBox
                  width={43}
                  height={43}
                  borderRadius={type === "song" ? "zero" : "s"}
                  overflow="hidden"
                >
                  <LArtwork
                    url={images}
                    size={43}
                    imageSize="small"
                    borderRadius={type === "song" ? "zero" : "s"}
                    blurhash={blurhash}
                    showTracklistChip={item.type === "song" && item.isSetList}
                    tracklistChipCompact
                  />
                  {item.type === "song" && item.private ? (
                    <ExclusiveRibbon containerSize={43} variant="compact" />
                  ) : null}
                </LBox>
              )}
            </LBox>

            <LBox
              justifyContent={hasSubtitle ? "space-between" : "center"}
              maxWidth={
                item.type === "song" &&
                item.private &&
                unlockedPrivateSongIds?.includes(item.id)
                  ? "70%"
                  : "76%"
              }
            >
              <LText variant="listItem" numberOfLines={1}>
                {title}
              </LText>
              {hasSubtitle ? (
                <LText variant="listItem-subheader" pt="s1" numberOfLines={1}>
                  {subtitle}
                </LText>
              ) : null}
            </LBox>
            {type === "song" && (
              <LBox alignItems="flex-end" flex={1} pr="s0_5">
                <LBox flexDirection="row" gap="s2" alignItems="center">
                  {isLocked ? (
                    <LText color="exclusiveGold" variant="Body/B1/SemiBold">
                      Unlock
                    </LText>
                  ) : null}
                  {!isLocked ? (
                    <OptionsButton
                      selectedCollection={{
                        collectionId: item.albums.at(0)?.id ?? "",
                        collectionType: "album",
                      }}
                      selectedCatalogSong={item}
                      sentryEventName="search_result_song_options"
                    />
                  ) : null}
                </LBox>
              </LBox>
            )}
          </LBox>
        </LBox>
      </LBox>
    </TransitionTrigger>
  );
};

export const SearchHistoryResult = ({
  item,
  onAddSearch,
  onRemoveSearch,
  userId,
}: {
  item: Search;
  onAddSearch: (search: Pick<Search, "id" | "type">) => void;
  onRemoveSearch: (id: string) => void;
  userId: string | undefined;
}) => {
  const playerActions = usePlayerActions();

  const playFromCollection = playerActions.playFromCollection;
  const theme = useTheme();

  const onPress = async () => {
    void rudderstack.track("press", { value: "search_history_pressed" });

    onAddSearch({
      type: item.type,
      id: item.id,
    });

    if (item.type === "song") {
      playFromCollection({
        songId: item.id,
        collectionType: "album",
        collectionId: item.albums.at(0)?.id ?? "",
        collectionName: item.albums.at(0)?.title,
      });
    } else {
      router.navigate({
        pathname: "/(app)/(tabs)/explore/[collectionType]/[collectionId]",
        params: {
          collectionType: item.type,
          collectionId: item.id,
        },
      });
    }
    Keyboard.dismiss();
  };

  const onRemovePress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    void rudderstack.track("press", { value: "search_history_remove" });
    onRemoveSearch(item.id);
  };

  const { type } = item;

  let title = "";
  let subtitle = "";
  let images: (string | undefined | null)[] = [""];
  let blurhash: string | undefined | null;

  switch (type) {
    case "song": {
      images = [item.albums.at(0)?.image];
      title = item.title;
      subtitle = `Song • ${item.artists.map((artist) => artist.name)?.join(", ") ?? ""}`;
      break;
    }
    case "album": {
      images = [item.image];
      title = item.title;
      subtitle = `Album • ${item.artistDisplayText}`;
      break;
    }
    case "artist": {
      images = [item.image];
      title = item.name;
      subtitle = "Artist";
      break;
    }
    case "playlist": {
      blurhash = item.blurhash;
      images = item.images || [];
      title = item.title;
      const isOwner = item.user !== null && item.user.id === userId;
      if (isOwner) {
        subtitle = "Playlist • You";
      } else {
        subtitle = `Playlist • ${item.user?.name ?? "Unknown"}`;
      }
      break;
    }
  }

  const hasSubtitle = subtitle.length > 0;

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        historyStyles.row,
        pressed ? historyStyles.pressed : null,
      ]}
    >
      <View style={historyStyles.content}>
        <View style={historyStyles.media}>
          {type === "artist" ? (
            <UserAvatar
              image={images.at(0)}
              size={43}
              borderColor="buttonTextPrimary"
              imageSize="small"
              blurhash={blurhash}
              transition={120}
            />
          ) : (
            <LArtwork
              url={images}
              size={43}
              imageSize="small"
              borderRadius={type === "song" ? "zero" : "s"}
              blurhash={blurhash}
              transition={120}
              showTracklistChip={item.type === "song" && item.isSetList}
              tracklistChipCompact
            />
          )}
        </View>

        <View style={historyStyles.text}>
          <Text
            numberOfLines={1}
            style={[
              historyStyles.title,
              {
                color: theme.colors.textPrimary,
                fontFamily: theme.textVariants.listItem.fontFamily,
                fontSize: theme.textVariants.listItem.fontSize,
                lineHeight: theme.textVariants.listItem.lineHeight,
              },
            ]}
          >
            {title}
          </Text>
          {hasSubtitle ? (
            <Text
              numberOfLines={1}
              style={[
                historyStyles.subtitle,
                {
                  color: theme.colors.subText,
                  fontFamily:
                    theme.textVariants["listItem-subheader"].fontFamily,
                  fontSize: theme.textVariants["listItem-subheader"].fontSize,
                  lineHeight:
                    theme.textVariants["listItem-subheader"].lineHeight,
                  paddingTop: theme.spacing.s1,
                },
              ]}
            >
              {subtitle}
            </Text>
          ) : null}
        </View>
      </View>

      <Pressable
        onPress={onRemovePress}
        hitSlop={12}
        style={({ pressed }) => [
          historyStyles.remove,
          pressed ? historyStyles.pressed : null,
        ]}
      >
        <View style={historyStyles.cross}>
          <View
            style={[
              historyStyles.crossLine,
              historyStyles.crossLineA,
              { backgroundColor: theme.colors.white },
            ]}
          />
          <View
            style={[
              historyStyles.crossLine,
              historyStyles.crossLineB,
              { backgroundColor: theme.colors.white },
            ]}
          />
        </View>
      </Pressable>
    </Pressable>
  );
};

const historyStyles = StyleSheet.create({
  row: {
    alignItems: "center",
    borderRadius: 4,
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    paddingLeft: 8,
    paddingVertical: 4,
  },
  pressed: {
    opacity: 0.75,
  },
  content: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
  },
  media: {
    height: 43,
    marginRight: 12,
    width: 43,
  },
  text: {
    justifyContent: "center",
    maxWidth: "76%",
  },
  title: {},
  subtitle: {},
  remove: {
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  cross: {
    height: 15,
    width: 15,
  },
  crossLine: {
    height: 1.5,
    left: 0,
    position: "absolute",
    top: 6.75,
    width: 15,
  },
  crossLineA: {
    transform: [{ rotate: "45deg" }],
  },
  crossLineB: {
    transform: [{ rotate: "-45deg" }],
  },
});

export default SearchResult;
